import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;
const todayInput = () => new Date().toISOString().split('T')[0];

const parseList = (response) => Array.isArray(response?.Data) ? response.Data : Array.isArray(response) ? response : [];
const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;
const dateParts = (value) => {
  const date = value ? new Date(value) : new Date();
  return { FromDay: date.getDate(), FromMonth: date.getMonth() + 1, FromYear: date.getFullYear() };
};
const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
};

const DriverFollowup = () => {
  const [date, setDate] = useState(todayInput());
  const [appliedDate, setAppliedDate] = useState(todayInput());
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.DriverFollowup({
        ...dateParts(appliedDate),
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      console.error('Driver followup error:', err);
      setRows([]);
      setTotalCount(0);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  }, [appliedDate, currentPage]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filterData = () => {
    setAppliedDate(date);
    setCurrentPage(1);
  };

  const refreshData = () => {
    const next = todayInput();
    setDate(next);
    setAppliedDate(next);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Driver Followup | {formatDate(appliedDate)}</h1>
          <p className="mt-1 text-sm text-gray-500">Daily driver pickup and delivery followup summary.</p>
        </div>
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          </div>
          <div className="flex gap-3 md:col-span-2">
            <button type="button" onClick={filterData} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button type="button" onClick={refreshData} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900">Driver Followup List</h2>
          <p className="text-sm text-gray-500">Showing {rows.length} of {totalCount} drivers</p>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1200px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Mobile No 1</Th>
                <Th>Mobile No 2</Th>
                <Th>Vehicle Number</Th>
                <Th>Total Pickup</Th>
                <Th>Total Picked</Th>
                <Th>Total Pickup Cancel</Th>
                <Th>Total Pickup Return</Th>
                <Th>Total Delivery</Th>
                <Th>Total Delivered</Th>
                <Th>Total Cancelled</Th>
                <Th>Total Returned</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={13} text="Loading drivers..." />
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={13} text="No driver followup found." />
              ) : (
                rows.map((row, index) => (
                  <tr key={row.Id} className="hover:bg-gray-50">
                    <Td>{(currentPage - 1) * PAGE_SIZE + index + 1}</Td>
                    <Td strong>{row.Name || '-'}</Td>
                    <Td>{row.MobileNo1 || '-'}</Td>
                    <Td>{row.MobileNo2 || '-'}</Td>
                    <Td>{row.Vehicle?.Number || '-'}</Td>
                    <Td>{row.TotalPickup ?? 0}</Td>
                    <Td>{row.TotalPicked ?? 0}</Td>
                    <Td>{row.TotalPickupCancel ?? 0}</Td>
                    <Td>{row.TotalPickupReturn ?? 0}</Td>
                    <Td>{row.TotalDelivery ?? 0}</Td>
                    <Td>{row.TotalDeliver ?? 0}</Td>
                    <Td>{row.TotalCancelled ?? 0}</Td>
                    <Td>{row.TotalReturned ?? 0}</Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span>Showing {rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage <= 1 || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
            <span className="px-3 py-1.5">Page {currentPage} of {totalPages}</span>
            <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage >= totalPages || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children }) => <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{children}</th>;
const Td = ({ children, strong = false }) => <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{children}</td>;
const EmptyRow = ({ colSpan, text }) => <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">{text}</td></tr>;

export default DriverFollowup;
